import { IAlertProps } from "@components/alert/Alert.types";
import BusyIndicator from "@components/busyIndicator";
import { IDialogProps } from "@components/dialog";
import Dialog from "@components/dialog/Dialog";
import { isValidDate } from "@components/inputs/date/utils";
import FieldsWrapper from "@components/inputs/field/FieldsWrapper";
import { ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import SmartField, { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormGroup from "@components/smart/smartFormGroup";
import BindingContext from "@odata/BindingContext";
import {
    IPrSocialEventEntity,
    IPrSocialEventMonthDetailEntity,
    PrSocialEventEntity,
    PrSocialEventMonthDetailEntity,
    PrSocialEventMonthIntervalEntity
} from "@odata/GeneratedEntityTypes";
import { PrSocialEventMonthDetailTypeCode } from "@odata/GeneratedEnums";
import {
    getNewMonthDetailItem,
    prepareMonthDetailIntervalsAfterTypeChange,
    TSocialEventFormStorage
} from "@pages/payroll/socialEvents/SocialEvents.utils";
import {
    isOfCaregivingDetailType,
    MonthDetailIntervalsFastEntryDef
} from "@pages/payroll/socialEvents/SocialEventsDef";
import React from "react";

import { getUtcDayjs } from "../../../types/Date";
import { FormAlertContainer } from "../../../views/formView/FormView.styles";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";

export interface IProps extends Pick<IDialogProps, "width" | "minWidth"> {
    storage: TSocialEventFormStorage;
    onClose: (isConfirmed: boolean) => void;
    title?: string;
    alert?: IAlertProps;
    isLoading?: boolean;
}

export default class CaregivingEndEventDialog extends React.PureComponent<IProps> {
    _endEventMonthDetailBc: BindingContext;

    constructor(props: IProps) {
        super(props);

        const { storage } = props;
        const entity = storage.data.entity as IPrSocialEventEntity;
        if (!entity.MonthDetails) {
            entity.MonthDetails = [];
        }
        let endMD = entity.MonthDetails?.find(md => md.IsEnd);
        if (!endMD) {
            endMD = getNewMonthDetailItem(storage);
            endMD.IsEnd = true;
            entity.MonthDetails.push(endMD);
        }
        this._endEventMonthDetailBc = storage.data.bindingContext.navigate(PrSocialEventEntity.MonthDetails).addKey(endMD);
    }

    get isLoading(): boolean {
        return this.props.isLoading || !this.props.storage.loaded || this.props.storage.isBusy();
    }

    getFieldBindingContexts(): BindingContext[] {
        const { storage } = this.props;
        return [
            storage.data.bindingContext.navigate(PrSocialEventEntity.DateEventEnd),
            storage.data.bindingContext.navigate(PrSocialEventEntity.WorkedHoursOnLastDay),
            this._endEventMonthDetailBc?.navigate(PrSocialEventMonthDetailEntity.MonthDetailType)
        ];
    }

    getBindingContextsForValidation(): BindingContext[] {
        const { storage } = this.props;
        const intervalBcs: BindingContext[] = [];
        const endEvent = storage.getValue(this._endEventMonthDetailBc) as IPrSocialEventMonthDetailEntity;
        const baseBc = this._endEventMonthDetailBc.navigate(PrSocialEventMonthDetailEntity.Intervals);
        endEvent.Intervals?.forEach((i) => {
            const intervalBc = baseBc.addKey(i);
            intervalBcs.push(
                intervalBc.navigate(PrSocialEventMonthIntervalEntity.DateFrom),
                intervalBc.navigate(PrSocialEventMonthIntervalEntity.DateTo)
            );
        });
        return [
            ...this.getFieldBindingContexts(),
            ...intervalBcs
        ];
    }

    handleConfirmDialog = async (): Promise<void> => {
        const bcs = this.getBindingContextsForValidation();
        const errors = await this.props.storage.validateFields(bcs);
        if (!errors?.some(e => !!e)) {
            this.props.onClose(true);
            return;
        }
        this.props.storage.refreshFields();
    };

    handleCancel = (): void => {
        this.props.onClose(false);
    };

    handleBlur = (args: ISmartFieldBlur): Promise<void> => {
        return this.props.storage.validateField(args.bindingContext)
            .then(() => this.forceUpdate());
    };

    handleChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        storage.handleChange(e);
        const path = e.bindingContext.getPath();
        if (path === PrSocialEventEntity.DateEventEnd) {
            // set Month and Year according to the date end
            const md = storage.getValue(this._endEventMonthDetailBc) as IPrSocialEventMonthDetailEntity;
            const dateEventEnd = e.value as Date;

            const socialEventStartDate = storage.getValueByPath(PrSocialEventEntity.DateEventStart) as Date;
            if (dateEventEnd && socialEventStartDate && getUtcDayjs(dateEventEnd).isSame(getUtcDayjs(socialEventStartDate), "day")) {
                storage.setValueByPath(PrSocialEventEntity.WorkedHoursOnLastDay, storage.getValueByPath(PrSocialEventEntity.WorkedHoursOnFirstDay));
                storage.setValue(this._endEventMonthDetailBc?.navigate(PrSocialEventMonthDetailEntity.MonthDetailType), PrSocialEventMonthDetailTypeCode.WholeTimeOfAbsence);
                storage.setValue(this._endEventMonthDetailBc?.navigate(PrSocialEventMonthDetailEntity.Intervals), []);
            }

            if (md && isValidDate(dateEventEnd)) {
                md.Month = dateEventEnd.getMonth() + 1;
                md.Year = dateEventEnd.getFullYear();
                storage.setValue(this._endEventMonthDetailBc, md);
            }
        }
        if (path === PrSocialEventMonthDetailEntity.MonthDetailType) {
            const intervalsBc = this._endEventMonthDetailBc.navigate(PrSocialEventMonthDetailEntity.Intervals);
            prepareMonthDetailIntervalsAfterTypeChange(storage, intervalsBc, e.value as PrSocialEventMonthDetailTypeCode);
        }
        storage.refreshFields(e.triggerAdditionalTasks);
        this.forceUpdate();
    };

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent): void => {
        this.props.storage.handleLineItemsAction(args);
        this.props.storage.refreshFields();
    };

    handleLineItemsChange = async (args: ISmartFieldChange): Promise<void> => {
        this.props.storage.handleLineItemsChange(args);
        this.props.storage.refreshFields();
    }

    renderFields = (): React.ReactElement => {
        const { storage } = this.props;
        const rowBcs = this.getFieldBindingContexts();
        const bindingContext = this._endEventMonthDetailBc?.navigate(PrSocialEventMonthDetailEntity.Intervals);
        const _isOnlyInSelectedDays = isOfCaregivingDetailType(PrSocialEventMonthDetailTypeCode.OnlyInSelectedDays)({ storage, bindingContext });
        return (<>
            <FieldsWrapper>
                {rowBcs.map((bindingContext) => {
                        const path = bindingContext.getPath(true);
                        const renderProps = {
                            storage,
                            bindingContext,
                            onBlur: this.handleBlur,
                            onChange: this.handleChange
                        };

                        return (
                            <SmartField key={path} {...renderProps} />
                        );
                    }
                )}
            </FieldsWrapper>
            {_isOnlyInSelectedDays && (
                <SmartFormGroup storage={storage}
                                def={MonthDetailIntervalsFastEntryDef}
                                bindingContext={this._endEventMonthDetailBc}
                                onBlur={this.handleBlur}
                                onChange={this.handleChange}
                                onLineItemsAction={this.handleLineItemsAction}
                                onLineItemsChange={this.handleLineItemsChange}
                />
            )}
        </>);
    };

    render = (): React.ReactElement => (
        <Dialog title={this.props.title}
                width={"550px"}
                minWidth={this.props.minWidth}
                onClose={this.handleCancel}
                onConfirm={this.handleConfirmDialog}
                footer={
                    <ConfirmationButtons
                        confirmText={this.props.storage.t("Common:General.Confirm")}
                        onConfirm={this.handleConfirmDialog}
                        onCancel={this.handleCancel}
                        useWrapper={false}/>
                }>
            {this.props.alert && (
                <FormAlertContainer {...this.props.alert}/>
            )}
            {!this.isLoading ? (
                <>
                    {this.props.children}
                    <FieldsWrapper isColumn>
                        {this.renderFields()}
                    </FieldsWrapper>
                </>
            ) : <BusyIndicator/>}
        </Dialog>
    );
}